import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import useScrollToTop from '../hooks/useScrollToTop';

const OtherCitizenship: React.FC = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  useScrollToTop();

  const programs = {
    fastNaturalization: [] as Array<{
      title: string;
      description: string;
      backgroundColor: string;
      path: string;
    }>,
    residenceVisas: [
      {
        title: t('nav.croatia'),
        description: t('residency.croatia.description'),
        backgroundColor: '#e91e63',
        path: '/other-citizenship/croatia',
      },
      {
        title: t('nav.france'),
        description: t('residency.france.description'),
        backgroundColor: '#ff5722',
        path: '/other-citizenship/france',
      },
      {
        title: t('nav.greece'),
        description: t('residency.greece.description'),
        backgroundColor: '#673ab7',
        path: '/other-citizenship/greece',
      },
      {
        title: t('nav.japan'),
        description: t('residency.japan.description'),
        backgroundColor: '#009688',
        path: '/other-citizenship/japan',
      },
      {
        title: t('nav.malta'),
        description: t('residency.malta.description'),
        backgroundColor: '#2196f3',
        path: '/other-citizenship/malta',
      },
    ],
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))',
          backgroundColor: '#673ab7',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '50vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h2" component="h1" gutterBottom>
              {t('residency.hero.title')}
            </Typography>
            <Typography variant="h5" gutterBottom>
              {t('residency.hero.subtitle')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Programs Section */}
      <Container sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          {t('residency.programs.title')}
        </Typography>
        <Typography variant="body1" align="center" sx={{ mb: 6 }}>
          {t('residency.programs.description')}
        </Typography>

        {/* Fast Naturalization Section - Only show if there are programs */}
        {/* {programs.fastNaturalization.length > 0 && (
          <>
            <Typography variant="h4" component="h3" align="center" gutterBottom sx={{ mt: 4 }}>
              {t('residency.programs.fastNaturalization')}
            </Typography>
            <Grid container spacing={4}>
              {programs.fastNaturalization.map((program, index) => (
                <Grid item xs={12} md={4} key={program.path}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.2 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box
                        sx={{
                          height: 200,
                          backgroundColor: program.backgroundColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                        }}
                      >
                        <Typography variant="h6">{program.title}</Typography>
                      </Box>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h5" component="h3" gutterBottom>
                          {program.title}
                        </Typography>
                        <Typography variant="body1" paragraph>
                          {program.description}
                        </Typography>
                        <Button
                          component={Link}
                          to={program.path}
                          variant="contained"
                          color="primary"
                          fullWidth
                        >
                          {t('residency.programs.learnMore')}
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </>
        )} */}

        {/* Residence Visas Section */}
        <Typography variant="h4" component="h3" align="center" gutterBottom sx={{ mt: 6 }}>
          {t('residency.programs.residenceVisas')}
        </Typography>
        <Grid container spacing={4}>
          {programs.residenceVisas.map((program, index) => (
            <Grid item xs={12} md={4} key={program.path}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                whileHover={{ scale: 1.02 }}
              >
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box
                    sx={{
                      height: 200,
                      backgroundColor: program.backgroundColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                    }}
                  >
                    <Typography variant="h6">{program.title}</Typography>
                  </Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" component="h3" gutterBottom>
                      {program.title}
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {program.description}
                    </Typography>
                    <Button
                      component={Link}
                      to={program.path}
                      variant="contained"
                      color="primary"
                      fullWidth
                    >
                      {t('residency.programs.learnMore')}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            {t('residency.benefits.title')}
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {[
              t('residency.benefits.freedom'),
              t('residency.benefits.education'),
              t('residency.benefits.healthcare'),
              t('residency.benefits.business'),
              t('residency.benefits.travel'),
              t('residency.benefits.family'),
            ].map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card>
                    <CardContent>
                      <Typography variant="h6" component="h3" gutterBottom>
                        {benefit}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default OtherCitizenship; 